import cx from 'classnames';
import {Form, Formik, FormikProps} from 'formik';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {settingsApi} from 'api';
import {
    AiProviderSelect, Button, Card, NumericFieldFormik, PredefinedNotifierType, ToggleFormik, UnsavedChangesLeavePrompt
} from 'components';
import {ExternalIntegrationPurpose, ValidationCode} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {AccountAiSettings} from 'types';
import {apiUtils, dataUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.ACTIVITIES;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

// Same tracks as the activity cards so every provider dropdown on the page shares one vertical line.
const ROW_CLASS = 'tw-grid tw-items-end tw-gap-x-4 tw-gap-y-3 md:tw-min-h-[3rem] ' +
    'md:tw-grid-cols-[14rem_12rem_minmax(0,1fr)]';
const GROUP_CLASS = 'tw-flex tw-flex-col md:tw-gap-3 -md:tw-gap-6';
const GROUP_TITLE_CLASS = 'tw-ts-overline tw-text-default-dimmed tw-mb-2';
const GROUP_SEPARATED_CLASS = 'tw-mt-4 tw-pt-4 tw-border-t tw-border-default';

type Props = {
    settings: AccountAiSettings;
    providers: Partial<Record<ExternalIntegrationPurpose, string[]>>;
    onSaved: () => void;
};

type FormTemplateProps = {
    loading: boolean;
} & Props & FormikProps<AccountAiSettings>;

const FormTemplate: React.FC<FormTemplateProps> = ({providers, loading, values, initialValues, isValid, submitForm}) => {
    const {t} = useTranslation();

    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [values, initialValues]);

    return (
        <Form>
            {/* Whose audio is streamed is stored once for the whole account, not per activity, so these
                two sit here rather than next to each activity's own streaming toggle. */}
            <div className={GROUP_TITLE_CLASS}>{t(T_KEYS.LABELS.SPEECH_TO_TEXT)}</div>
            <div className={ROW_CLASS}>
                <ToggleFormik name='agentStreamAudio.enabled' label={t(T_KEYS.LABELS.STREAM_FROM_AGENT)} />
                <ToggleFormik
                    name='clientStreamAudio.enabled'
                    label={t(T_KEYS.LABELS.STREAM_FROM_CLIENT)}
                    className='md:tw-col-start-3'
                />
            </div>

            <div className={cx(GROUP_CLASS, GROUP_SEPARATED_CLASS)}>
                <div className={ROW_CLASS}>
                    <ToggleFormik name='translation.enabled' label={t(T_KEYS.LABELS.TRANSLATION)} />
                    <AiProviderSelect
                        name='translation.provider'
                        providers={providers[ExternalIntegrationPurpose.Translation]}
                        placeholder={t(T_KEYS.LABELS.PROVIDER)}
                        disabled={!values.translation?.enabled}
                        required={values.translation?.enabled}
                    />
                </div>

                <div className={ROW_CLASS}>
                    <ToggleFormik name='aiSuggestions.enabled' label={t(T_KEYS.LABELS.AI_SUGGESTIONS)} />
                    <AiProviderSelect
                        name='aiSuggestions.provider'
                        providers={providers[ExternalIntegrationPurpose.AgentTextChatSuggestions]}
                        placeholder={t(T_KEYS.LABELS.PROVIDER)}
                        disabled={!values.aiSuggestions?.enabled}
                        required={values.aiSuggestions?.enabled}
                    />
                    <NumericFieldFormik
                        name='aiSuggestions.count'
                        label={t(T_KEYS.LABELS.SUGGESTIONS_COUNT)}
                        min={1}
                        max={5}
                        className='tw-w-32'
                        disabled={!values.aiSuggestions?.enabled}
                    />
                </div>
            </div>

            <div className='tw-text-right tw-mt-controls'>
                <Button
                    color='primary'
                    disabled={hasNoChanges || !isValid}
                    loading={loading}
                    onClick={submitForm}
                >
                    {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                </Button>
            </div>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

export const AccountSection: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const {settings, onSaved} = props;

    const {send: save, loading} = useApiData(
        settingsApi.updateAccountAi, {notificationType: PredefinedNotifierType.Save, onSuccess: onSaved}
    );

    const handleSubmit = useCallback((values: AccountAiSettings) => save(values), [save]);

    // An enabled feature without a provider is enabled and silently dead, so the provider becomes required the
    // moment its own toggle goes on.
    const validationSchema = useMemo(() => {
        const providerRequiredWhenEnabled = () => Yup.string().nullable().when('enabled', {
            is: true,
            then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required}))
        });
        const countRequiredWhenEnabled = () => Yup.number().nullable().when('enabled', {
            is: true,
            then: (schema) => schema
                .required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required}))
                .min(1, apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Minimum, expected: 1}))
                .max(5, apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Maximum, expected: 5}))
        });

        return Yup.object().shape({
            translation: Yup.object().shape({provider: providerRequiredWhenEnabled()}),
            aiSuggestions: Yup.object().shape({provider: providerRequiredWhenEnabled(), count: countRequiredWhenEnabled()})
        });
    }, [t]);

    return (
        <Card className='tw-mb-4'>
            <div className='tw-ts-h5-bold tw-mb-1'>{t(T_KEYS.ACCOUNT.TITLE)}</div>
            <div className='tw-text-sm tw-text-default-dimmed tw-mb-4'>{t(T_KEYS.ACCOUNT.DESCRIPTION)}</div>
            <Formik
                initialValues={settings || {}}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {(formikProps: FormikProps<AccountAiSettings>) =>
                    <FormTemplate {...props} {...formikProps} loading={loading} />}
            </Formik>
        </Card>
    );
};
